import React, { useState } from 'react';
import { Socio } from '../types';
import { supabase } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';

interface Props {
  socio: Socio;
  onSaved?: (s: Socio) => void;
}

const EditProfile: React.FC<Props> = ({ socio, onSaved }) => {
  // defensively read initial values from `socio` in case it's a Proxy or throws
  let initial = {
    nombre: '',
    apellido: '',
    telefono: '',
    email: '',
    documento: '',
    instagram: '',
    notas: ''
  } as Record<string, any>;

  try {
    if (socio && typeof socio === 'object') {
      initial = {
        nombre: socio.nombre ?? '',
        apellido: socio.apellido ?? '',
        telefono: socio.telefono ?? '',
        email: socio.email ?? '',
        documento: socio.documento ?? '',
        instagram: socio.instagram ?? '',
        notas: socio.notas ?? ''
      };
    }
  } catch (err) {
    // guard: if reading `socio` throws, fall back to empty values
    // eslint-disable-next-line no-console
    console.warn('EditProfile: failed to read socio:', err);
  }

  const [nombre, setNombre] = useState(initial.nombre);
  const [apellido, setApellido] = useState(initial.apellido);
  const [telefono, setTelefono] = useState(initial.telefono);
  const [email, setEmail] = useState(initial.email);
  const [documento, setDocumento] = useState(initial.documento);
  const [instagram, setInstagram] = useState(initial.instagram);
  const [notas, setNotas] = useState(initial.notas);
  const [saving, setSaving] = useState(false);
  const ui = useUI();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!socio || !socio.id) {
        if (ui && typeof ui.showToast === 'function') ui.showToast('error', 'Socio inválido');
        setSaving(false);
        return;
      }

      const payload = {
        nombre: (nombre || '').trim(),
        apellido: (apellido || '').trim(),
        telefono: (telefono || '').trim(),
        email: email ? (email as string).trim() : null,
        documento: documento ? (documento as string).trim() : null,
        instagram: instagram ? (instagram as string).trim() : null,
        notas: notas || ''
      } as any;

      if (!supabase || typeof supabase.from !== 'function') {
        if (ui && typeof ui.showToast === 'function') ui.showToast('error', 'Servicio no disponible');
        setSaving(false);
        return;
      }

      const { data, error } = await supabase.from('socios').update(payload).eq('id', socio.id).select().single();
      if (error) throw error;
      if (ui && typeof ui.showToast === 'function') ui.showToast('success', 'Perfil guardado correctamente');
      if (onSaved && data) onSaved(data as Socio);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error updating socio profile:', err);
      if (ui && typeof ui.showToast === 'function') ui.showToast('error', err?.message || 'No se pudo guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Nombre</label>
          <input value={nombre} onChange={e => setNombre(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Apellido</label>
          <input value={apellido} onChange={e => setApellido(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Teléfono</label>
          <input value={telefono} onChange={e => setTelefono(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input value={email ?? ''} onChange={e => setEmail(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Documento</label>
          <input value={documento ?? ''} onChange={e => setDocumento(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Instagram</label>
          <input value={instagram ?? ''} onChange={e => setInstagram(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notas</label>
        <textarea value={notas} onChange={e => setNotas(e.target.value)} className="mt-1 block w-full border rounded-md p-2" rows={3} />
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm">
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};

export default EditProfile;
